import { addAffiliation, getAffiliations } from "./affiliation";
import { getEcommerceDataFromSkus } from "./get-ecommerce-data-from-skus";
import { getEcommerceDataFromCart } from "./get-ecommerce-data-from-cart";
import { z } from "zod/v4";
import { EcommerceItemSchema } from "./item-schema";
import { AffiliationSchema } from "./affiliation-schema";

export type EcommerceItemSource = z.infer<typeof EcommerceItemSourceSchema>;

export const EcommerceItemSourceCartSchema = z.object({
  type: z.literal("cart"),
  cartId: z.string(),
});
export const EcommerceItemSourceItemsSchema = z.object({
  type: z.literal("items"),
  skus: z.union([z.string(), z.array(z.string())]),
});

export const EcommerceItemSourceSchema = z.discriminatedUnion("type", [
  EcommerceItemSourceCartSchema,
  EcommerceItemSourceItemsSchema,
]);

const RequestSchema = z.object({
  source: EcommerceItemSourceSchema,
  affiliation: AffiliationSchema.optional(),
});

// EcommerceData
export const EcommerceDataSchema = z.object({
  items: z.array(EcommerceItemSchema),
  currency: z.enum(["NOK"]).prefault("NOK"),
  value: z.number().optional(),
  transaction_id: z.string().optional(),
  cart_id: z.string().optional(),
  shipping: z.number().optional(),
  tax: z.number().optional(),
  discount: z.number().optional(),
  coupon: z.string().optional(),
  coupons: z.array(z.string()).optional(),
});

async function getEcommerceData(source: EcommerceItemSource) {
  if (source.type === "cart") return getEcommerceDataFromCart(source.cartId);
  return getEcommerceDataFromSkus(
    typeof source.skus === "string" ? [source.skus] : source.skus,
  );
}

export type EcommerceData = z.infer<typeof EcommerceDataSchema>;

export async function getEcommerceDataAffiliated(
  input: z.infer<typeof RequestSchema>,
) {
  const { affiliation, source } = RequestSchema.parse(input);
  if (affiliation) {
    await addAffiliation(affiliation);
  }

  const [ecommerce, affiliations] = await Promise.all([
    getEcommerceData(source),
    getAffiliations(),
  ]);
  return EcommerceDataSchema.parse({
    ...ecommerce,
    items: ecommerce.items.map((item) => {
      const affiliation = affiliations.find(
        (affiliation) => affiliation.sku === item.item_id,
      );

      if (!affiliation) return item;
      return {
        ...item,
        index: affiliation.item_list_item_index,
        item_list_id: affiliation.item_list_id,
        item_list_name: affiliation.item_list_name,
        creative_name: affiliation.creative_name,
        creative_slot: affiliation.creative_slot,
        promotion_id: affiliation.promotion_id,
        promotion_name: affiliation.promotion_name,
      };
    }),
  });
}
